import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { 
  Target, Eye, Star, Quote,
  Building2, Users, Newspaper
} from "lucide-react";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useSiteContent } from "@/hooks/use-site-content";

interface TeamMember {
  name: string;
  role: string;
  image: string | null;
}

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  rating: number;
}

interface Stat {
  value: string;
  label: string;
}

const defaultTeamMembers: TeamMember[] = [
  { name: "Finlay Rees", role: "Founder & CEO", image: null },
  { name: "Kiera Hunt", role: "Chief Editor", image: null },
  { name: "Leon Sharp", role: "Head of Content", image: null },
  { name: "Dana Cotton", role: "Senior Analyst", image: null },
  { name: "Marcus Webb", role: "Reporter, Business", image: null },
  { name: "Rebecca Turner", role: "Reporter, Travel", image: null },
  { name: "Tiffany Mack", role: "Reporter, Lifestyle", image: null },
  { name: "Elmer Holland", role: "Reporter, Technology", image: null },
];

const defaultTestimonials: Testimonial[] = [
  {
    quote: "FM World's reporting consistently exemplifies journalistic integrity and a commitment to uncovering the truth, making it a cornerstone for anyone looking to be genuinely informed.",
    author: "Sandy Osborne",
    role: "Analyst",
    rating: 5,
  },
  {
    quote: "Through detailed research and engaging storytelling, FM World offers a unique perspective on news that matters, providing readers with a comprehensive view of the world.",
    author: "Dean Holloway",
    role: "Business Owner",
    rating: 5,
  },
];

const defaultStats: Stat[] = [
  { value: "25+", label: "Years of Experience" },
  { value: "90K+", label: "Worldwide Subscribers" },
];

function TeamMemberCard({ member }: { member: TeamMember }) {
  return (
    <div className="text-center group" data-testid={`team-member-${member.name.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="relative mb-4 mx-auto w-40 h-40 overflow-hidden rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800">
        {member.image ? (
          <img 
            src={member.image} 
            alt={member.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Users className="h-16 w-16 text-slate-400 dark:text-slate-500" />
          </div>
        )}
      </div>
      <h3 className="font-semibold text-lg">{member.name}</h3>
      <p className="text-sm text-muted-foreground">{member.role}</p>
    </div>
  );
}

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <Card className="h-full" data-testid={`testimonial-${testimonial.author.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6 flex flex-col h-full">
        <Quote className="h-8 w-8 text-primary mb-4 flex-shrink-0" />
        <p className="text-muted-foreground flex-1 mb-6 italic">
          "{testimonial.quote}"
        </p>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center">
            <Users className="h-6 w-6 text-slate-400" />
          </div>
          <div>
            <div className="flex items-center gap-1 mb-1">
              {[...Array(testimonial.rating)].map((_, i) => (
                <Star key={i} className="h-3 w-3 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="font-semibold text-sm">{testimonial.author}</p>
            <p className="text-xs text-muted-foreground">{testimonial.role}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatCard({ stat }: { stat: Stat }) {
  return (
    <div className="text-center">
      <div className="text-4xl font-bold text-primary mb-1" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
        {stat.value}
      </div>
      <div className="text-sm text-muted-foreground uppercase tracking-wide">{stat.label}</div>
    </div>
  );
}

export default function AboutPage() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const { toast } = useToast();
  const { getContent, getContentInfo, getJsonContent } = useSiteContent("about");

  const renderContent = (key: string, defaultValue: string, className?: string, isRichTextDefault: boolean = false) => {
    const info = getContentInfo(key, defaultValue);
    if (info.isRichText || isRichTextDefault) {
      return <span className={className} dangerouslySetInnerHTML={{ __html: info.value }} />;
    }
    return <span className={className}>{info.value}</span>;
  };

  const teamMembers = getJsonContent<TeamMember[]>("team_members", defaultTeamMembers);
  const testimonials = getJsonContent<Testimonial[]>("testimonials", defaultTestimonials);
  const stats = getJsonContent<Stat[]>("stats", defaultStats);

  type SC = { visible?: boolean; heading?: string; subtitle?: string; fieldVisible?: Record<string, boolean> };
  const fv = (cfg: SC, key: string) => cfg.fieldVisible?.[key] !== false;
  const secAboutHero          = getJsonContent<SC>("section_hero", {});
  const secAboutWelcome       = getJsonContent<SC>("section_welcome", {});
  const secAboutTeam          = getJsonContent<SC>("section_team", {});
  const secAboutTestimonials  = getJsonContent<SC>("section_testimonials", {});
  const secAboutNewsletter    = getJsonContent<SC>("section_newsletter", {});

  const newsletterMutation = useMutation({
    mutationFn: async (data: { email: string; name?: string }) => {
      const res = await apiRequest("POST", "/api/waitlist", data);
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Subscribed!",
        description: "You'll receive our latest updates.",
      });
      setEmail("");
      setName("");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Something went wrong.",
        variant: "destructive",
      });
    },
  });

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    newsletterMutation.mutate({ email, name: name || undefined });
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-7xl px-4 pt-4">
        <PageBreadcrumb items={[{ label: "About" }]} />
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-primary-foreground py-20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50"></div>
        <div className="mx-auto max-w-7xl px-4 text-center relative">
          <Badge variant="secondary" className="mb-4">
            {getContent("hero_badge", "About Us")}
          </Badge>
          {fv(secAboutHero, 'heading') && (
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 [&_p]:m-0">
              {renderContent("hero_title", "About Us")}
            </h1>
          )}
          {fv(secAboutHero, 'subtitle') && (
            <p className="text-lg opacity-90 max-w-2xl mx-auto [&_p]:m-0">
              {renderContent("hero_subtitle", "We embrace innovation in how we gather and present news, radically pushing the boundaries of traditional media to deliver our readers' experiences.")}
            </p>
          )}
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              {fv(secAboutWelcome, 'heading') && (
                <h2 className="text-3xl sm:text-4xl font-bold [&_p]:m-0">
                  {renderContent("welcome_heading", "Welcome to FM World, your premier source for insightful news and captivating features.")}
                </h2>
              )}
              <div className="text-muted-foreground space-y-4 [&_p]:mb-4">
                {renderContent("welcome_body", `<p>Founded with a mission to enlighten, entertain, and inform, FM World delivers up-to-the-minute news and deep-dive articles across a diverse range of topics. From the latest developments in technology and business to evolving stories in lifestyle, travel, and culture, we aim to cover the spectrum of news with rigor and integrity.</p><p>Whether you're interested in sharp analyses, inspiring stories, or the latest scoops, FM World is your go-to destination. Join us daily on our journey—where news comes to life, and every story is worth telling.</p>`, undefined, true)}
              </div>
              
              {/* Stats */}
              <div className="flex flex-wrap gap-8 sm:gap-12 pt-6 border-t">
                {stats.map((stat, index) => (
                  <StatCard key={index} stat={stat} />
                ))}
              </div>
            </div>
            
            {/* Image Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-[4/5] rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center">
                  <Users className="h-16 w-16 text-slate-400" />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="aspect-[4/5] rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center">
                  <Newspaper className="h-16 w-16 text-slate-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-muted/30">
        <div className="mx-auto max-w-7xl px-4">
          <div className="text-center mb-12">
            {fv(secAboutTeam, 'heading') && (
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 [&_p]:m-0">
                {renderContent("team_heading", "Meet the Team Behind FM World")}
              </h2>
            )}
            {fv(secAboutTeam, 'subtitle') && (
              <p className="text-muted-foreground max-w-2xl mx-auto [&_p]:m-0">
                {renderContent("team_description", "Our diverse team of expert journalists and editors is dedicated to delivering insightful, accurate, and engaging content to our global audience.")}
              </p>
            )}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <TeamMemberCard key={index} member={member} />
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="aspect-[4/3] rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center overflow-hidden">
              <Building2 className="h-24 w-24 text-slate-400" />
            </div>
            
            {/* Content */}
            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Target className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{getContent("mission_title", "Our Mission")}</h3>
                  <p className="text-muted-foreground [&_p]:m-0">
                    {renderContent("mission_body", "Our mission is to deliver high-quality news and insightful analysis that empowers our readers. We strive to provide timely, accurate, and comprehensive reporting that enlightens and informs public discourse. By bridging gaps in understanding across diverse communities, we aim to be a catalyst for knowledge and change.")}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Eye className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{getContent("vision_title", "Our Vision")}</h3>
                  <p className="text-muted-foreground [&_p]:m-0">
                    {renderContent("vision_body", "Our vision is to be the leading online news platform that readers turn to for excellence. We aspire to shape the future of digital news by fostering a well-informed public equipped to tackle the challenges of our times. Through innovative storytelling and pioneering technology, we envision a world where information strengthens community ties and promotes global understanding.")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-muted/30">
        <div className="mx-auto max-w-7xl px-4">
          <div className="text-center mb-12">
            {fv(secAboutTestimonials, 'heading') && (
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 [&_p]:m-0">
                {renderContent("testimonials_heading", "Uncover the stories that shape our world")}
              </h2>
            )}
            {fv(secAboutTestimonials, 'subtitle') && (
              <p className="text-muted-foreground max-w-2xl mx-auto [&_p]:m-0">
                {renderContent("testimonials_description", "We uphold the highest standards of journalistic integrity and ethical reporting, ensuring that truth and transparency prevail in all our content. We are committed to engaging with our readers, listening to their concerns, and adapting our content to meet their needs.")}
              </p>
            )}
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="mx-auto max-w-4xl px-4 text-center">
          {fv(secAboutNewsletter, 'heading') && (
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 [&_p]:m-0">
              {renderContent("newsletter_heading", "Stay up to date on the latest news")}
            </h2>
          )}
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto mt-6">
            <Input
              type="text"
              placeholder="Your Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
              data-testid="input-newsletter-name"
            />
            <Input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
              required
              data-testid="input-newsletter-email"
            />
            <Button 
              type="submit" 
              variant="secondary"
              disabled={newsletterMutation.isPending}
              data-testid="button-newsletter-subscribe"
            >
              {newsletterMutation.isPending ? "..." : "Subscribe"}
            </Button>
          </form>
          <p className="text-xs opacity-70 mt-4 [&_p]:m-0">
            {renderContent("newsletter_disclaimer", "By pressing the Subscribe button, you confirm that you have read our Privacy Policy.")}
          </p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
